import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Loading from '../../../components/common/Loading';
import useNotify from '../../../hooks/useNotify';
import DateRangeFilter from '../components/DateRangeFilter';
import HistoryDataTable from '../components/HistoryDataTable';
import SensorStatusBadge from '../components/SensorStatusBadge';
import SensorTrendChart from '../components/SensorTrendChart';
import SeverityBadge from '../components/SeverityBadge';
import { useSensors } from '../context/SensorContext';
import shmSensorService from '../services/shmSensorService';
import { exportSensorHistoryCsv } from '../utils/exportCsv';
import {
  formatAxisValue,
  formatTemperature,
  formatTimestamp,
} from '../utils/formatSensor';
import '../styles/monitoring.css';

const PAGE_SIZE = 50;

const today = new Date().toISOString().split('T')[0];
const sevenAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  .toISOString()
  .split('T')[0];

const AXIS_OPTIONS = [
  { value: 'all', label: 'All Axis' },
  { value: 'xAxis', label: 'X-Axis' },
  { value: 'yAxis', label: 'Y-Axis' },
  { value: 'zAxis', label: 'Z-Axis' },
];

export default function SensorHistoryPage() {
  const { sensorId } = useParams();
  const { showToast } = useNotify();
  const { getBridgeName, latestSensorData } = useSensors();

  const [startDate, setStartDate] = useState(sevenAgo);
  const [endDate, setEndDate] = useState(today);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [axisView, setAxisView] = useState('all');

  const liveReading = latestSensorData[sensorId];

  const loadHistory = useCallback(async () => {
    if (!sensorId) return;
    setLoading(true);
    try {
      const from = startDate ? `${startDate}T00:00:00.000Z` : undefined;
      const to = endDate ? `${endDate}T23:59:59.999Z` : undefined;
      const response = await shmSensorService.getHistory(sensorId, {
        from,
        to,
        limit: 5000,
      });
      setHistory(response.data || []);
      setPage(1);
    } catch (e) {
      showToast(e.message, 'error');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [sensorId, startDate, endDate, showToast]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const paginatedReadings = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return history.slice(start, start + PAGE_SIZE);
  }, [history, page]);

  const handleExport = useCallback(() => {
    if (!history.length) {
      showToast('No data to export', 'info');
      return;
    }
    const serial = liveReading?.sensorSerialNo || sensorId;
    exportSensorHistoryCsv(history, `${serial}-history.csv`);
  }, [history, liveReading, sensorId, showToast]);

  const sensorMeta = useMemo(() => {
    if (liveReading) return liveReading;
    return history[history.length - 1] || null;
  }, [liveReading, history]);

  const bridgeName = sensorMeta ? getBridgeName(sensorMeta.bridgeId) : '—';

  if (loading && !history.length && !liveReading) {
    return <Loading message="Loading sensor history…" />;
  }

  return (
    <div className="shm-monitoring-page">
      <div className="shm-page-header">
        <div>
          <Link to="/monitoring/live" className="shm-back-link">
            <i className="ti ti-arrow-left" /> Back to Live
          </Link>
          <h1 className="shm-page-title">
            {sensorMeta?.sensorType || 'Sensor'} History
          </h1>
          <p className="shm-page-subtitle">
            {sensorMeta?.sensorSerialNo || sensorId} · {bridgeName}
          </p>
        </div>
        <div className="shm-page-actions">
          <button type="button" className="btn btn-teal" onClick={handleExport}>
            <i className="ti ti-download" /> Export CSV
          </button>
        </div>
      </div>

      {liveReading && (
        <div className="info-bar shm-live-summary">
          <span><strong>Live:</strong> {formatTimestamp(liveReading.timestamp)}</span>
          <span><strong>Temp:</strong> {formatTemperature(liveReading.temperature)}</span>
          <span><strong>X/Y/Z:</strong> {formatAxisValue(liveReading.xAxis)} / {formatAxisValue(liveReading.yAxis)} / {formatAxisValue(liveReading.zAxis)}</span>
          <span><strong>Severity:</strong> <SeverityBadge severity={liveReading.severity} /></span>
          <SensorStatusBadge timestamp={liveReading.timestamp} />
        </div>
      )}

      <div className="card shm-history-controls">
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartChange={setStartDate}
          onEndChange={setEndDate}
          onApply={loadHistory}
          loading={loading}
        />
        <label className="shm-filter-label">
          Chart Axis:
          <select value={axisView} onChange={(e) => setAxisView(e.target.value)}>
            {AXIS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="shm-charts-grid">
        <SensorTrendChart
          title={`${sensorMeta?.sensorType || 'Sensor'} — Axis Trend`}
          data={history}
          mode="axis"
          axisView={axisView}
        />
        <SensorTrendChart
          title="Temperature Trend"
          data={history}
          mode="temperature"
        />
      </div>

      <div className="shm-charts-grid shm-charts-grid--quad">
        <SensorTrendChart
          title="X Axis Trend"
          data={history}
          mode="axis"
          axisView="xAxis"
          height={220}
        />
        <SensorTrendChart
          title="Y Axis Trend"
          data={history}
          mode="axis"
          axisView="yAxis"
          height={220}
        />
        <SensorTrendChart
          title="Z Axis Trend"
          data={history}
          mode="axis"
          axisView="zAxis"
          height={220}
        />
        <SensorTrendChart
          title="Temperature Trend"
          data={history}
          mode="temperature"
          height={220}
        />
      </div>

      <HistoryDataTable
        readings={paginatedReadings}
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={history.length}
        onPageChange={setPage}
      />
    </div>
  );
}
